import React, { useCallback, useEffect, useRef, useState } from 'react';
import { usePermissions } from 'react-admin';
import { httpClient } from '../utils/dataProvider';
import { API_BASE_URL } from '../utils/config';
import { hasAccess } from '../utils/permissions';
import { formatUsd } from '../utils/referralSettings';
import {
    PageShell, PageHeader, Surface, MetricCard, RowText, RowMeta, StatusPill,
    StatePanel, Empty, ActionButton, DetailGrid, DetailItem,
} from '../components/ui';

// Per-inviter referral reward investigation view for support staff.
// Strictly read-only: every request below is a GET against the admin
// referral read endpoints, and the page renders no mutate controls.

export const REWARD_STATUSES = Object.freeze(['PENDING', 'RELEASED', 'CAPPED', 'REVERSED', 'VOIDED']);
export const PAGE_SIZE = 20;
export const MAX_PAGE_SIZE = 100; // server hard cap
const USER_ID_RE = /^[a-f0-9]{24}$/i;
const GET = Object.freeze({ method: 'GET' });
const NAME_UNAVAILABLE = 'Name unavailable';

const enc = encodeURIComponent;
export const buildSummaryUrl = userId => `${API_BASE_URL}/admin/referrals/users/${enc(userId)}/summary`;
export const buildRewardUrl = rewardId => `${API_BASE_URL}/admin/referrals/rewards/${enc(rewardId)}`;
export const buildRewardsUrl = (userId, { page = 1, limit = PAGE_SIZE, status } = {}) => {
    const safePage = Math.max(1, Math.floor(Number(page)) || 1);
    const safeLimit = Math.min(MAX_PAGE_SIZE, Math.max(1, Math.floor(Number(limit)) || PAGE_SIZE));
    const params = new URLSearchParams({ page: String(safePage), limit: String(safeLimit) });
    // Only exact enum values are ever sent; anything else would earn a 400 INVALID_STATUS.
    if (REWARD_STATUSES.includes(status)) params.set('status', status);
    return `${API_BASE_URL}/admin/referrals/users/${enc(userId)}/rewards?${params.toString()}`;
};

// Phone-number lookup. Reuses the admin user list's existing search contract
// (GET /users?filter=<text>&fields=phone — the same db.checkQueryString filter
// the Cancellation Console's user search uses). No new endpoint.
// Only digits (and an optional leading +) are ever sent, so nothing the agent
// types can reach the server-side regex as a metacharacter.
const PHONE_INPUT_RE = /^\+?[\d\s\-().]+$/;
const PHONE_LOOKUP_LIMIT = 20;
export const phoneDigits = raw => String(raw || '').replace(/\D/g, '');
export const isPhoneQuery = raw => {
    const text = String(raw || '').trim();
    if (!text || USER_ID_RE.test(text) || !PHONE_INPUT_RE.test(text)) return false;
    const digits = phoneDigits(text);
    return digits.length >= 7 && digits.length <= 15;
};
export const buildPhoneLookupUrl = raw => {
    const params = new URLSearchParams({ filter: phoneDigits(raw), fields: 'phone', limit: String(PHONE_LOOKUP_LIMIT) });
    return `${API_BASE_URL}/users?${params.toString()}`;
};
// The server filter is a substring match, so keep only accounts whose stored
// phone has EXACTLY the digits typed. Never guess between several accounts.
export const matchUsersByPhone = (docs, raw) => {
    const want = phoneDigits(raw);
    if (!want || !Array.isArray(docs)) return [];
    return docs.filter(doc => doc && phoneDigits(doc.phone) === want && USER_ID_RE.test(String(doc._id || doc.id || '')));
};

const money = minor => (Number.isSafeInteger(minor) && minor >= 0 ? formatUsd(minor) : '—');
const fmtInt = n => Number(n || 0).toLocaleString('en-US');
// The backend stores UTC and support quotes these times (releaseAt above all)
// to users, so they are always rendered in UTC with an explicit label, never
// in the admin browser's local zone.
export const fmtDate = iso => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return `${d.toLocaleString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
        hourCycle: 'h23', timeZone: 'UTC',
    })} UTC`;
};
const orDash = v => (v === null || v === undefined || v === '' ? '—' : String(v));
const inviteeName = invitee => (invitee && invitee.displayName) || NAME_UNAVAILABLE;
const statusCount = (summary, status) => {
    const entry = summary && summary.rewards && summary.rewards.byStatus && summary.rewards.byStatus[status];
    return (entry && entry.count) || 0;
};

// Human-readable text for the statusReason codes the backend actually writes.
// Producers (skilzBackend):
//   CAP_EXHAUSTED:<PER_DEAL|MONTHLY|LIFETIME>  services/referralRewardService.js
//     (`CAP_EXHAUSTED:${result.cappedBy}`, CAPPED only; cappedBy values from
//     services/referralRewardCalculator.js CAPPED_BY)
//   RELEASE_BLOCKED:<reason>                   services/referralRewardRelease.js
//     (VOIDED only; reasons from assessEligibility: DEAL_CANCELLED:<request
//     status|cancelledAt>, DEAL_REFUNDED:transaction_<wallet row status>,
//     REFERRAL_REVOKED)
// Anything else is shown raw as "unrecognised" — never given an invented meaning.
const CAP_LABELS = Object.freeze({
    PER_DEAL: 'Per-deal cap',
    MONTHLY: 'Monthly cap',
    LIFETIME: 'Lifetime cap',
});
const CAP_TEXT = Object.freeze({
    PER_DEAL: "The per-deal cap reduced this reward to $0.00.",
    MONTHLY: "The inviter's monthly cap for this cap month was already used up, so this reward was $0.00.",
    LIFETIME: "The inviter's lifetime cap was already used up, so this reward was $0.00.",
});
export const UNRECOGNISED_REASON = 'Unrecognised reason code. Shown exactly as stored.';
export const describeStatusReason = code => {
    if (typeof code !== 'string' || code.trim() === '') return null;
    const parts = code.split(':');
    const [kind, sub, detail] = parts;
    if (kind === 'CAP_EXHAUSTED' && parts.length === 2 && CAP_TEXT[sub]) {
        return { known: true, cap: CAP_LABELS[sub], text: CAP_TEXT[sub] };
    }
    if (kind === 'RELEASE_BLOCKED') {
        if (sub === 'REFERRAL_REVOKED' && parts.length === 2) {
            return { known: true, text: 'Voided at release time: the referral was revoked.' };
        }
        if (sub === 'DEAL_CANCELLED' && parts.length === 3 && detail) {
            const how = detail === 'cancelledAt' ? 'the request has a cancellation timestamp' : `the request status is ${detail}`;
            return { known: true, text: `Voided at release time: the qualifying deal was cancelled (${how}).` };
        }
        if (sub === 'DEAL_REFUNDED' && parts.length === 3 && /^transaction_.+/.test(detail || '')) {
            const txStatus = detail.slice('transaction_'.length);
            return { known: true, text: `Voided at release time: the qualifying payment is no longer live (wallet row status ${txStatus}).` };
        }
    }
    return { known: false, text: UNRECOGNISED_REASON };
};

const ReasonText = ({ code, testId }) => {
    const info = describeStatusReason(code);
    if (!info) return '—';
    return (
        <span data-testid={testId}>
            <code>{code}</code>
            {' — '}
            <span>{info.cap ? <strong>{info.cap} exhausted. </strong> : null}{info.text}</span>
        </span>
    );
};

const errorCode = err => (err && err.body && err.body.errors && err.body.errors.msg) || null;
export const describeError = (err, fallback) => {
    const status = err && err.status;
    const code = errorCode(err);
    if (status === 403) return 'You do not have access to referral rewards.';
    if (status === 401) return 'Your admin session has expired. Sign in again.';
    switch (code) {
        case 'USER_NOT_FOUND': return 'No user with that id.';
        case 'INVALID_USER_ID': return 'That is not a valid user id.';
        case 'REFERRAL_REWARD_NOT_FOUND': return 'That reward no longer exists.';
        case 'INVALID_REWARD_ID': return 'That is not a valid reward id.';
        case 'INVALID_STATUS': return 'That reward status filter is not valid.';
        default: return fallback;
    }
};

const INPUT_ERROR = 'Enter a 24-character hexadecimal user id or a phone number.';
const PHONE_LOOKUP_FALLBACK = 'Could not look up that phone number.';
const LOAD_FALLBACK = 'Could not load referral rewards for this inviter.';
const SUMMARY_FALLBACK = "Could not load this inviter's referral summary.";
const DETAIL_FALLBACK = 'Could not load this reward.';

export default function ReferralRewards({ match }) {
    const routeUserId = (match && match.params && match.params.userId) || '';
    const { permissions, loaded } = usePermissions();
    const roles = (permissions && permissions.adminRoles) || [];
    const allowed = Boolean(loaded && hasAccess('referral-rewards', roles));

    const initialValid = USER_ID_RE.test(routeUserId);
    const [inputText, setInputText] = useState(routeUserId);
    const [inputError, setInputError] = useState(
        routeUserId && !initialValid ? 'Enter a 24-character hexadecimal user id.' : '',
    );
    const [inviterId, setInviterId] = useState(initialValid ? routeUserId.toLowerCase() : '');
    const [statusFilter, setStatusFilter] = useState('');
    const [page, setPage] = useState(1);
    const [summary, setSummary] = useState(null);
    const [rewards, setRewards] = useState(null);
    // Two independent endpoints, two independent errors: a failure in one must
    // never hide the other's data.
    const [summaryError, setSummaryError] = useState('');
    const [rewardsError, setRewardsError] = useState('');
    const [rewardsLoading, setRewardsLoading] = useState(false);
    const [selectedRewardId, setSelectedRewardId] = useState(null);
    const [detail, setDetail] = useState(null);
    const [detailError, setDetailError] = useState('');
    const [detailLoading, setDetailLoading] = useState(false);
    // Bumped when the SAME inviter id is re-submitted: forces a genuine refetch
    // of everything on screen (the support agent's retry after a failure).
    const [reloadNonce, setReloadNonce] = useState(0);

    // The inviter the latest request generation belongs to. Responses whose
    // inviter no longer matches are dropped so a slow earlier request can never
    // paint over a newer one.
    const inviterRef = useRef(inviterId);
    inviterRef.current = inviterId;

    const applyInviter = useCallback(raw => {
        const id = String(raw || '').trim();
        if (!USER_ID_RE.test(id)) {
            setInputError(INPUT_ERROR);
            return;
        }
        const next = id.toLowerCase();
        setInputError('');
        setInputText(id);
        if (next === inviterRef.current) {
            // Same inviter: keep filter/page/selection but refetch all of it.
            setReloadNonce(n => n + 1);
            return;
        }
        // New inviter: drop everything belonging to the previous one synchronously.
        inviterRef.current = next;
        setInviterId(next);
        setStatusFilter('');
        setPage(1);
        setSummary(null);
        setRewards(null);
        setSummaryError('');
        setRewardsError('');
        setSelectedRewardId(null);
        setDetail(null);
        setDetailError('');
    }, []);

    // Phone lookup state. A token drops any lookup superseded by a newer submit.
    const [phoneLookup, setPhoneLookup] = useState({ loading: false, resolved: null });
    const lookupTokenRef = useRef(0);

    const submitQuery = useCallback(raw => {
        const text = String(raw || '').trim();
        const token = ++lookupTokenRef.current;
        if (!isPhoneQuery(text)) {
            setPhoneLookup({ loading: false, resolved: null });
            applyInviter(text); // unchanged 24-hex path (and its validation)
            return;
        }
        setInputError('');
        setPhoneLookup({ loading: true, resolved: null });
        httpClient(buildPhoneLookupUrl(text), { ...GET })
            .then(({ json }) => {
                if (token !== lookupTokenRef.current) return;
                const matches = matchUsersByPhone(json && json.docs, text);
                if (matches.length !== 1) {
                    setPhoneLookup({ loading: false, resolved: null });
                    setInputError(matches.length === 0
                        ? 'No user with that phone number. Enter it in full international format, e.g. +966…'
                        : `${matches.length} users have that phone number. Enter the user id instead.`);
                    return;
                }
                const user = matches[0];
                const userId = String(user._id || user.id);
                applyInviter(userId);
                setInputText(text); // keep what the agent typed in the box
                setPhoneLookup({ loading: false, resolved: { phone: user.phone, name: user.name || '', userId: userId.toLowerCase() } });
            })
            .catch(err => {
                console.error('ReferralRewards phone lookup failed:', err);
                if (token !== lookupTokenRef.current) return;
                setPhoneLookup({ loading: false, resolved: null });
                setInputError(describeError(err, PHONE_LOOKUP_FALLBACK));
            });
    }, [applyInviter]);

    // Follow route-param changes (deep links) after the first render.
    const lastRouteRef = useRef(routeUserId);
    useEffect(() => {
        if (lastRouteRef.current === routeUserId) return;
        lastRouteRef.current = routeUserId;
        if (routeUserId) applyInviter(routeUserId);
    }, [routeUserId, applyInviter]);

    const loadSummary = useCallback(id => httpClient(buildSummaryUrl(id), { ...GET }), []);
    const loadRewards = useCallback((id, opts) => httpClient(buildRewardsUrl(id, opts), { ...GET }), []);
    const loadReward = useCallback(id => httpClient(buildRewardUrl(id), { ...GET }), []);

    useEffect(() => {
        if (!allowed || !inviterId) return undefined;
        let alive = true;
        const forInviter = inviterId;
        setSummaryError('');
        loadSummary(forInviter)
            .then(({ json }) => {
                if (!alive || inviterRef.current !== forInviter) return;
                setSummary(json || null);
            })
            .catch(err => {
                console.error('ReferralRewards summary failed:', err);
                if (!alive || inviterRef.current !== forInviter) return;
                setSummary(null);
                setSummaryError(describeError(err, SUMMARY_FALLBACK));
            });
        return () => { alive = false; };
    }, [allowed, inviterId, reloadNonce, loadSummary]);

    useEffect(() => {
        if (!allowed || !inviterId) return undefined;
        let alive = true;
        const forInviter = inviterId;
        setRewardsLoading(true);
        setRewardsError('');
        loadRewards(forInviter, { page, limit: PAGE_SIZE, status: statusFilter || undefined })
            .then(({ json }) => {
                if (!alive || inviterRef.current !== forInviter) return;
                setRewards(json || null);
            })
            .catch(err => {
                console.error('ReferralRewards rewards list failed:', err);
                if (!alive || inviterRef.current !== forInviter) return;
                setRewards(null);
                setRewardsError(describeError(err, LOAD_FALLBACK));
            })
            .finally(() => { if (alive) setRewardsLoading(false); });
        return () => { alive = false; };
    }, [allowed, inviterId, page, statusFilter, reloadNonce, loadRewards]);

    useEffect(() => {
        if (!allowed || !selectedRewardId) return undefined;
        let alive = true;
        const forReward = selectedRewardId;
        setDetailLoading(true);
        setDetailError('');
        setDetail(null);
        loadReward(forReward)
            .then(({ json }) => {
                if (!alive) return;
                const reward = json && json.reward;
                if (!reward || reward.id !== forReward) {
                    setDetailError('That reward no longer exists.');
                    return;
                }
                setDetail(reward);
            })
            .catch(err => {
                console.error('ReferralRewards reward detail failed:', err);
                if (!alive) return;
                setDetailError(describeError(err, DETAIL_FALLBACK));
            })
            .finally(() => { if (alive) setDetailLoading(false); });
        return () => { alive = false; };
    }, [allowed, selectedRewardId, reloadNonce, loadReward]);

    const items = (rewards && Array.isArray(rewards.items)) ? rewards.items : [];

    // A selection must never outlive its row (filter/page change).
    useEffect(() => {
        if (!selectedRewardId || !rewards) return;
        if (!items.some(item => item && item.id === selectedRewardId)) {
            setSelectedRewardId(null);
            setDetail(null);
            setDetailError('');
        }
    }, [rewards, items, selectedRewardId]);

    if (!loaded) return <PageShell><StatePanel state="loading">Loading permissions…</StatePanel></PageShell>;
    if (!hasAccess('referral-rewards', roles)) {
        return <PageShell><StatePanel state="error">You do not have permission to view this page.</StatePanel></PageShell>;
    }

    const changeFilter = value => {
        setStatusFilter(value);
        setPage(1);
    };
    const selectRow = id => setSelectedRewardId(id);
    const rowKeyDown = (event, id) => {
        if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
            event.preventDefault();
            selectRow(id);
        }
    };

    const currentPage = (rewards && rewards.page) || page;
    const totalPages = Math.max(1, (rewards && rewards.totalPages) || 1);
    const hasNextPage = Boolean(rewards && rewards.hasNextPage) && currentPage < totalPages;
    const prevDisabled = currentPage <= 1 || rewardsLoading;
    const nextDisabled = !hasNextPage || rewardsLoading;
    const rewardTotals = (summary && summary.rewards) || {};
    const referralTotals = (summary && summary.referrals) || {};
    const shownDetail = detail && detail.id === selectedRewardId ? detail : null;

    return (
        <PageShell>
            <PageHeader eyebrow="Support" title="Referral Rewards"
                description="Look up one inviter's referrals and the rewards they earned from them." />

            <p className="sk-muted" style={{ margin: '0 0 16px', fontSize: 12, lineHeight: 1.5 }}>
                <strong>Note:</strong> This view is read-only investigation data. Amounts are the inviter's referral reward, not a wallet balance. PENDING rewards are held until their releaseAt time before they can be released.
            </p>
            <p className="sk-muted" data-testid="cap-limitation" style={{ margin: '0 0 16px', fontSize: 12, lineHeight: 1.5 }}>
                <strong>Caps:</strong> a status reason names a cap only when that cap cut the reward to $0.00 (status CAPPED). This view does not get the uncapped amount or the cap limits from the API, so a cap that only <em>partly</em> reduced a PENDING or RELEASED reward cannot be seen here.
            </p>

            <Surface title="Inviter" description="Enter the inviter's phone number or user id, or open this page from a user's profile.">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 520 }}>
                    <label className="sk-field">
                        Inviter phone number or user id
                        <input aria-label="Inviter phone number or user id" className="sk-control" value={inputText}
                            spellCheck={false} autoComplete="off" placeholder="Phone number or 24-hex user id"
                            aria-invalid={inputError ? 'true' : 'false'}
                            onChange={event => { setInputText(event.target.value); setInputError(''); }}
                            onKeyDown={event => { if (event.key === 'Enter') { event.preventDefault(); submitQuery(inputText); } }} />
                    </label>
                    {inputError && <span role="alert" style={{ color: '#b42318', fontSize: 13 }}>{inputError}</span>}
                    {phoneLookup.loading && <span role="status" style={{ fontSize: 13 }}>Looking up phone number…</span>}
                    {!phoneLookup.loading && phoneLookup.resolved && phoneLookup.resolved.userId === inviterId && (
                        <span data-testid="phone-resolved" style={{ fontSize: 13 }}>
                            Phone {phoneLookup.resolved.phone} is {phoneLookup.resolved.name || NAME_UNAVAILABLE} (<code>{phoneLookup.resolved.userId}</code>).
                        </span>
                    )}
                    <div>
                        <ActionButton type="button" variant="secondary" disabled={phoneLookup.loading} onClick={() => submitQuery(inputText)}>Load inviter</ActionButton>
                    </div>
                </div>
            </Surface>

            {!inviterId && !inputError && <Empty>Enter an inviter's phone number or user id to see their referral rewards.</Empty>}

            {inviterId && summaryError && (
                <div data-testid="summary-error"><StatePanel state="error">{summaryError}</StatePanel></div>
            )}

            {inviterId && !summaryError && !summary && <StatePanel state="loading">Loading inviter summary…</StatePanel>}

            {inviterId && !summaryError && summary && (
                <div data-testid="referral-summary" style={{ display: 'flex', gap: 16, flexWrap: 'wrap', margin: '16px 0' }}>
                    <MetricCard tone="purple" label="Total earned" value={money(rewardTotals.totalEarnedMinor)} detail="Pending + released" />
                    <MetricCard tone="green" label="Released" value={money(rewardTotals.totalReleasedMinor)} detail="Paid to the inviter" />
                    <MetricCard tone="orange" label="Pending" value={money(rewardTotals.totalPendingMinor)} detail="Held until releaseAt" />
                    <div data-testid="metric-referrals">
                        <MetricCard tone="blue" label="Referrals" value={fmtInt(referralTotals.total)}
                            detail={`${fmtInt(referralTotals.attributed)} attributed`} />
                    </div>
                </div>
            )}

            {inviterId && (
                <Surface title="Rewards" description="Newest first. Select a reward to see its full record.">
                    <div role="group" aria-label="Reward status filter" style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                        {[''].concat(REWARD_STATUSES).map(value => {
                            const active = statusFilter === value;
                            const count = value ? statusCount(summary, value) : (rewardTotals.total || 0);
                            return (
                                <button key={value || 'ALL'} type="button" aria-pressed={active}
                                    onClick={() => changeFilter(value)}
                                    style={{
                                        padding: '6px 12px', borderRadius: 999, fontSize: 12, fontWeight: 700, cursor: 'pointer',
                                        border: `1px solid ${active ? '#7c3aed' : 'var(--sk-border)'}`,
                                        background: active ? '#7c3aed' : 'var(--sk-surface)',
                                        color: active ? '#fff' : 'inherit',
                                    }}>
                                    {value || 'All'} {fmtInt(count)}
                                </button>
                            );
                        })}
                    </div>

                    {rewardsError && (
                        <div data-testid="rewards-error"><StatePanel state="error">{rewardsError}</StatePanel></div>
                    )}
                    {rewardsLoading && !rewards && <StatePanel state="loading">Loading rewards…</StatePanel>}
                    {rewards && items.length === 0 && (
                        <Empty>{statusFilter ? `No ${statusFilter} referral rewards for this inviter.` : 'No referral rewards for this inviter yet.'}</Empty>
                    )}

                    {items.length > 0 && (
                        <div role="listbox" aria-label="Referral rewards">
                            {items.map(item => {
                                const selected = item.id === selectedRewardId;
                                return (
                                    <div key={item.id} role="option" tabIndex={0} aria-selected={selected}
                                        className="sk-row is-clickable"
                                        onClick={() => selectRow(item.id)}
                                        onKeyDown={event => rowKeyDown(event, item.id)}
                                        style={selected ? { outline: '2px solid #7c3aed', outlineOffset: -2 } : undefined}>
                                        <div className="sk-row-main">
                                            <RowText>
                                                <strong>{orDash(item.reference)}</strong>
                                                <RowMeta>
                                                    <span>{inviteeName(item.invitee)}</span>
                                                    <span>{fmtDate(item.createdAt)}</span>
                                                </RowMeta>
                                                {item.statusReason && <RowMeta dim><ReasonText code={item.statusReason} testId="row-reason" /></RowMeta>}
                                            </RowText>
                                        </div>
                                        <div className="sk-row-side">
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                <strong>{money(item.amountMinor)}</strong>
                                                <StatusPill status={item.status}>{item.status}</StatusPill>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {(rewards || rewardsError) && (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, marginTop: 16 }}>
                            <ActionButton type="button" variant="secondary" aria-label="Previous page" disabled={prevDisabled}
                                onClick={() => setPage(Math.max(1, currentPage - 1))}>Previous</ActionButton>
                            <span style={{ fontSize: 13 }}>Page {currentPage} of {totalPages}</span>
                            <ActionButton type="button" variant="secondary" aria-label="Next page" disabled={nextDisabled}
                                onClick={() => setPage(currentPage + 1)}>Next</ActionButton>
                        </div>
                    )}
                </Surface>
            )}

            {inviterId && selectedRewardId && (
                <div data-testid="reward-detail">
                    <Surface title="Reward detail" description={`Reward ${selectedRewardId}`}>
                        {detailLoading && <StatePanel state="loading">Loading reward…</StatePanel>}
                        {detailError && <StatePanel state="error">{detailError}</StatePanel>}
                        {shownDetail && (
                            <DetailGrid>
                                <DetailItem label="Reference" value={orDash(shownDetail.reference)} />
                                <DetailItem label="Status">
                                    <StatusPill status={shownDetail.status}>{shownDetail.status}</StatusPill>
                                </DetailItem>
                                <DetailItem label="Status reason">
                                    <ReasonText code={shownDetail.statusReason} testId="detail-reason" />
                                </DetailItem>
                                <DetailItem label="Amount" value={`${money(shownDetail.amountMinor)} ${shownDetail.currency || ''}`.trim()} />
                                <DetailItem label="Cap month" value={orDash(shownDetail.capMonth)} />
                                <DetailItem label="Created at" value={fmtDate(shownDetail.createdAt)} />
                                <DetailItem label="Release at" value={fmtDate(shownDetail.releaseAt)} />
                                <DetailItem label="Released at" value={fmtDate(shownDetail.releasedAt)} />
                                <DetailItem label="Deal completed at" value={fmtDate(shownDetail.dealCompletedAt)} />
                                <DetailItem label="Invitee" value={inviteeName(shownDetail.invitee)} />
                                <DetailItem label="Invitee id" value={orDash(shownDetail.invitee && shownDetail.invitee.id)} />
                                <DetailItem label="Reward id" value={orDash(shownDetail.id)} />
                                <DetailItem label="Referral id" value={orDash(shownDetail.referralId)} />
                                <DetailItem label="Inviter id" value={orDash(shownDetail.inviterId)} />
                                <DetailItem label="Request id" value={orDash(shownDetail.requestId)} />
                                <DetailItem label="Transaction id" value={orDash(shownDetail.transactionId)} />
                            </DetailGrid>
                        )}
                    </Surface>
                </div>
            )}
        </PageShell>
    );
}
